import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Accordion } from "../../../../components/core/basic/Accordion";
import TrellisLink from "../../../../components/core/basic/TrellisLink";
import BreadcrumbDates from "../../../../components/core/blocks/BreadcrumbDates";
import { useMarketplaceLabel } from "../../../../utils/marketplaceConstants";
import MetricsCard from "../../../../components/custom/merchandise/MetricsCard";
import { createColumnBuilder } from "../../../../components/table/columnBuilder";
import { usePersistedTableState } from "../../../../components/table/usePersistedTableState";
import { idsToVisibilityState } from "../../../../components/table/utils";
import Flags from "country-flag-icons/react/3x2";
import { DatesProvider, useDates } from "../../../../dates/DatesContext";
import { useDateFormatter } from "../../../../i18n/useDateFormatter";
import AgencyOrgReportTable from "../../../../modules/organization/AgencyDashboardTable";
import { useGetV4OrgReportByDate } from "../../../../modules/perf_reports/api/organization/v4OrgReportByDate";
import { useGetV4OrgReportTotals } from "../../../../modules/perf_reports/api/organization/v4OrgReportTotals";
import DownloadReport from "../../../../modules/perf_reports/DownloadReport";
import { useMemo, useState } from "react";
import { Badge } from "react-bootstrap";
import { useAcosOrRoas } from "../../../../utils/acosOrRoas";
import { STAFF_DASHBOARD_BREADCRUMB_SPEC } from "../../../../utils/dashboardLinks";
import { snakeCaseToSentenceCase } from "../../../../utils/formatText";
import { useChangeOrg } from "../../../../hooks/useChangeOrg";
import { Center } from "../../../../../styled-system/jsx";
import { PageContainer } from "../../../../components/core/basic/PageContainer";
import { useAppSelector } from "../../../../redux/hooks";
import MarketplaceStatsFilter from "../../../../modules/merchandising/components/MarketplaceStatsFilter";
import getMetricSpec from "../../../../modules/merchandising/utils/metrics";
import { useStandardEntityMetrics } from "../../../../modules/merchandising/components/tables/metricUtils";
import OrgFinder from "../../../../components/custom/staff/OrgFinder";

const OrgCell = ({ orgData }) => {
  const { org_id, profile_type, market_place: flagNationCodeRaw, org_name, name, amazon_profile_name } = orgData;
  const changeOrgMutation = useChangeOrg();

  const flagNationCode = (() => {
    const val = flagNationCodeRaw;
    if (val === "USA") return "US";
    if (val === "UK") return "GB";
    return val;
  })();

  const displayName = org_name ?? name;
  const profileName = amazon_profile_name;
  const Flag = Flags[flagNationCode];

  return (
    <TrellisLink
      id="categoryPage"
      to={"#"}
      onClick={() => {
        changeOrgMutation.mutate(org_id, {
          onSuccess: () => {
            window.location.reload();
          },
        });
      }}
      style={{ textDecoration: "none", fontWeight: 500, fontSize: "1.5rem", cursor: "pointer" }}
    >
      <div className={"fs-standard text-underline-hover"}>
        <strong className={"text-underline-hover"}>{displayName}</strong>
        <Flag style={{ width: "18px" }} className="ms-3" />
      </div>
      <i className="pe-3" style={{ fontWeight: "light", color: "#666", fontSize: "1rem" }}>
        {profileName}
      </i>
      <Badge
        className={`badge bg-trellis-${profile_type === "seller" ? "fuchsia" : "mauve"} fs-6`}
        pill
        style={{ borderRadius: "5px" }}
      >
        {snakeCaseToSentenceCase(profile_type)}
      </Badge>
    </TrellisLink>
  );
};

const Dashboard = () => {
  const { t } = useTranslation(["merchandising"]);
  const getMarketplaceLabel = useMarketplaceLabel();
  const marketPlace = useAppSelector((state) => state.marketPlace);

  const [selectedMarketplace, setSelectedMarketplace] = useState(marketPlace.marketPlace);
  const [showHiddenOrgs, setShowHiddenOrgs] = useState(false);
  const { group, start, end, preStart, preEnd } = useDates();
  const longDate = useDateFormatter("long");

  const acosOrRoas = useAcosOrRoas();

  const marketplaceShortLabel = useMemo(
    () => getMarketplaceLabel(selectedMarketplace) ?? "",
    [getMarketplaceLabel, selectedMarketplace],
  );

  const breadCrumbItems = [STAFF_DASHBOARD_BREADCRUMB_SPEC];

  const columnHelper = createColumnBuilder();
  const standardMetricIds = useStandardEntityMetrics("organization");

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        id: "name",
        header: t(($) => $.merchandising["Organization"]),
        enableHiding: false,
        minSize: 100,
        size: 300,
        cell: (props) => {
          return <OrgCell orgData={props.row.original} marketPlace={selectedMarketplace} />;
        },
      }),
      ...standardMetricIds.map((metricId) => {
        return columnHelper.metric(metricId, { formatOptions: { marketPlace: selectedMarketplace } });
      }),
    ],
    [standardMetricIds, selectedMarketplace, columnHelper, t],
  );
  const defaultSelectedColumns = [
    "name",
    "sales",
    "units",
    acosOrRoas("acos", "roas"),
    "margin",
    "ad_sales",
    "ad_spend",
    "roms",
    "cost",
    "promo_cost",
    "clicks",
    "impressions",
    "ctr",
    "orders",
    "conversion_rate",
    "total_acos",
  ];

  const defaultHiddenCols = idsToVisibilityState(
    defaultSelectedColumns,
    columns.filter((column) => column.id).map((column) => column.id),
  );

  const initialStateConfig = {
    columnVisibility: {
      tags: false,
      ...defaultHiddenCols,
    },
    columnOrder: defaultSelectedColumns,
    columnPinning: {
      start: ["name"],
      end: [],
    },
    sorting: [{ id: "sales", desc: true }],
  };

  const { setters, initialState, state } = usePersistedTableState({
    persistKey: `allCustomersDashboard`,
    initialState: initialStateConfig,
  });

  const columnFilters = state?.columnFilters ?? [];
  const globalSearch = state?.globalFilter ?? undefined;

  const totalsQueryOptions = useGetV4OrgReportTotals(selectedMarketplace)({
    filters: columnFilters,
    search: globalSearch,
    allActive: true,
  });
  const totalsQuery = useQuery({
    ...totalsQueryOptions,
    placeholderData: keepPreviousData,
  });

  const byDateQueryOptions = useGetV4OrgReportByDate(selectedMarketplace)({
    filters: columnFilters,
    search: globalSearch,
    allActive: true,
  });
  const timelineQuery = useQuery({
    ...byDateQueryOptions,
    placeholderData: keepPreviousData,
  });

  const { overviewMetrics } = useMemo(() => {
    return {
      overviewMetrics: {
        columns: [],
        metrics: [
          getMetricSpec("sales"),
          getMetricSpec("units"),
          getMetricSpec("margin"),
          getMetricSpec("ad_sales"),
          getMetricSpec("ad_spend"),
          ...acosOrRoas(
            [getMetricSpec("total_acos"), getMetricSpec("acos")],
            [getMetricSpec("troas"), getMetricSpec("roas")],
          ),
          getMetricSpec("roms"),
          getMetricSpec("cost"),
          getMetricSpec("promo_cost"),
        ],
      },
    };
  }, [acosOrRoas]);

  const filters = {
    date_group: group,
    start_date: start.format("YYYY-MM-DD"),
    end_date: end.format("YYYY-MM-DD"),
    pre_start_date: preStart.format("YYYY-MM-DD"),
    pre_end_date: preEnd.format("YYYY-MM-DD"),
    empty: true,
    group_by: "org_id",
    all_active_orgs: true,
  };

  return (
    <>
      <BreadcrumbDates items={breadCrumbItems} />
      <PageContainer withBreadcrumb>
        <MetricsCard
          id={"agencyDashboard"}
          mobileStyle
          title={t(($) => $.merchandising["Overview"])}
          totals={{
            data: totalsQuery?.data?.data,
            loading: totalsQuery.isFetching && (!totalsQuery.data || totalsQuery.isPlaceholderData),
          }}
          timeline={{
            data: timelineQuery?.data?.data ?? [],
            loading: timelineQuery.isLoading,
          }}
          context={"organization"}
          initialSelectedMetric={["ad_sales", "ad_spend", "roas"]}
          metrics={overviewMetrics.metrics}
          group={group}
          showMetricsSelector
          byline={
            <>
              {longDate.format(start.toDate())} - {longDate.format(end.toDate())}
              {timelineQuery.isLoading && <i className="fa fa-spinner fa-spin"></i>}
            </>
          }
          inlineStatus={
            <MarketplaceStatsFilter
              selectedMarketplace={selectedMarketplace}
              setSelectedMarketplace={setSelectedMarketplace}
            />
          }
        >
          <Accordion
            title={t(($) => $.merchandising["Brands in {marketplace}"], { marketplace: marketplaceShortLabel })}
            defaultExpanded={true}
          >
            <AgencyOrgReportTable
              columns={columns}
              state={state}
              initialState={initialState}
              setters={setters}
              selectedMarketplace={selectedMarketplace}
              showHiddenOrgs={showHiddenOrgs}
              setShowHiddenOrgs={setShowHiddenOrgs}
              allActive={true}
            />
            <Center gap="large" padding="large">
              <DownloadReport
                url_path={"anomalies_report"}
                filters={{ ...filters, entity: "org", empty: false }}
                report_dl={"ads"}
                buttonText={t(($) => $.merchandising["Download Org Report"])}
              />
              <DownloadReport
                url_path={"anomalies_report"}
                filters={{ ...filters, entity: "category", empty: false }}
                report_dl={"ads"}
                buttonText={t(($) => $.merchandising["Download Category Report"])}
              />
              <DownloadReport
                url_path={"anomalies_report"}
                filters={{ ...filters, entity: "advertisement", empty: false }}
                report_dl={"ads"}
                buttonText={t(($) => $.merchandising["Download Advertisement Report"])}
              />
            </Center>
          </Accordion>
          <OrgFinder />
        </MetricsCard>
      </PageContainer>
    </>
  );
};

const AllCustomersDashboard = () => {
  return (
    <DatesProvider>
      <Dashboard />
    </DatesProvider>
  );
};

export const Route = createFileRoute("/_authenticated/user/staff/dashboard")({
  component: AllCustomersDashboard,
  staticData: { application: "Merchandising" },
});
